import { useState } from 'react';
import { Button } from '../../ui/Button';
import { Card, CardHeader, CardContent } from '../../ui/Card';
import type { MorningIntentionReflection } from '../../../types/checkin';
import { SunIcon, SparklesIcon } from '@heroicons/react/24/outline';

interface MorningReflectionProps {
  morningIntentions: Array<{
    questionId: string;
    question: string;
    answer: string | string[] | number | boolean;
  }>;
  onComplete: (reflections: Record<string, MorningIntentionReflection>) => void;
  onBack: () => void;
  onSkip: () => void;
}

export function MorningReflection({
  morningIntentions,
  onComplete,
  onBack,
  onSkip,
}: MorningReflectionProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [reflections, setReflections] = useState<Record<string, string>>({});

  const current = morningIntentions[currentIndex];
  const isLast = currentIndex === morningIntentions.length - 1;

  const handleReflection = (reflection: string) => {
    setReflections({
      ...reflections,
      [current.questionId]: reflection,
    });
  };

  const handleNext = () => {
    if (isLast) {
      // Convert to MorningIntentionReflection format
      const formattedReflections: Record<string, MorningIntentionReflection> = {};
      morningIntentions.forEach((intention) => {
        formattedReflections[intention.questionId] = {
          question: intention.question,
          morningAnswer: intention.answer,
          eveningReflection: reflections[intention.questionId] || '',
        };
      });
      onComplete(formattedReflections);
    } else {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    } else {
      onBack();
    }
  };

  // Format the answer for display
  const formatAnswer = (answer: string | string[] | number | boolean): string => {
    if (Array.isArray(answer)) {
      return answer.length > 0 ? answer.join(', ') : 'Not specified';
    }
    if (typeof answer === 'boolean') {
      return answer ? 'Yes' : 'Not specified';
    }
    if (typeof answer === 'string' && answer.trim() === '') {
      return 'Not specified';
    }
    if (typeof answer === 'number' && answer === 0) {
      return '0';
    }
    return String(answer);
  };

  return (
    <div className="min-h-screen bg-surface-50 dark:bg-surface-900 px-4 py-8">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-accent-100 dark:bg-accent-900/30 mb-2">
            <SunIcon className="w-6 h-6 text-accent-600 dark:text-accent-400" />
          </div>
          <h2 className="text-2xl font-bold text-surface-900 dark:text-surface-100">
            Morning Intentions Review
          </h2>
          <p className="text-surface-600 dark:text-surface-400">
            Reflect on how your day went compared to your morning intentions
          </p>
        </div>

        {/* Progress */}
        <div className="text-center text-sm text-surface-500 dark:text-surface-400">
          Intention {currentIndex + 1} of {morningIntentions.length}
        </div>

        {/* Morning Intention Card */}
        <Card variant="filled">
          <CardHeader
            title="This Morning You Said"
            action={<SparklesIcon className="w-5 h-5 text-accent-500" />}
          />
          <CardContent>
            <div className="space-y-3">
              <div>
                <p className="text-sm font-medium text-surface-600 dark:text-surface-400 mb-1">
                  {current.question}
                </p>
                <p className="text-lg text-surface-900 dark:text-surface-100 font-medium">
                  {formatAnswer(current.answer)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Reflection Card */}
        <Card variant="default">
          <CardHeader title="How Did It Go?" />
          <CardContent>
            <div className="space-y-3">
              <textarea
                rows={4}
                placeholder="Reflect on this intention... Did you follow through? What got in the way? What went well?"
                value={reflections[current.questionId] || ''}
                onChange={(e) => handleReflection(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-surface-300 dark:border-surface-600 bg-white dark:bg-surface-800 text-surface-900 dark:text-surface-100 resize-none focus:ring-2 focus:ring-accent-500 focus:border-accent-500"
              />
              <p className="text-xs text-surface-500 dark:text-surface-400">
                Optional - but helpful for tracking your growth
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex gap-3">
          <Button variant="secondary" size="lg" onClick={handlePrevious} className="flex-1">
            Back
          </Button>
          {currentIndex === 0 && (
            <Button variant="ghost" size="lg" onClick={onSkip} className="flex-1">
              Skip All
            </Button>
          )}
          <Button variant="primary" size="lg" onClick={handleNext} className="flex-1">
            {isLast ? 'Continue' : 'Next'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default MorningReflection;
